/**
 * Character-level diff for short values shown side by side in review tables.
 *
 * The import preview asks an admin to approve an overwrite, so the question
 * they actually have is "which characters differ" — a column-level "changed"
 * flag hides a single wrong digit inside a long grade string. Everything here
 * is character granularity on purpose; word granularity would highlight the
 * whole token "L12" when only "1" appeared.
 */

export type DiffSegmentType = "equal" | "remove" | "add";

export interface DiffSegment {
	type: DiffSegmentType;
	text: string;
}

/**
 * The LCS table is O(n·m); past this length the highlight stops being readable
 * anyway, so both sides are reported as one wholesale replacement instead.
 * Real values here are grade strings and names — tens of characters.
 */
const MAX_DIFF_LENGTH = 500;

/**
 * An equal run shorter than this, sitting between two changes, is folded into
 * the surrounding change. Without it a diff of two similar strings shreds into
 * alternating one-character highlights that read as noise.
 */
const MIN_EQUAL_RUN = 4;

/** Splits by code point, so a surrogate pair is never cut in half. */
function toChars(value: string): string[] {
	return Array.from(value);
}

/**
 * Classic LCS backtrack. Emits removals before additions at every divergence,
 * which is the order the unified rendering expects.
 */
function lcsSegments(oldChars: string[], newChars: string[]): DiffSegment[] {
	const n = oldChars.length;
	const m = newChars.length;
	const width = m + 1;
	const table = new Int32Array((n + 1) * width);

	for (let i = n - 1; i >= 0; i--) {
		for (let j = m - 1; j >= 0; j--) {
			table[i * width + j] =
				oldChars[i] === newChars[j]
					? table[(i + 1) * width + (j + 1)] + 1
					: Math.max(table[(i + 1) * width + j], table[i * width + (j + 1)]);
		}
	}

	const segments: DiffSegment[] = [];
	const push = (type: DiffSegmentType, text: string) => {
		const last = segments[segments.length - 1];
		if (last && last.type === type) last.text += text;
		else segments.push({ type, text });
	};

	let i = 0;
	let j = 0;
	while (i < n && j < m) {
		if (oldChars[i] === newChars[j]) {
			push("equal", oldChars[i]);
			i++;
			j++;
		} else if (table[(i + 1) * width + j] >= table[i * width + (j + 1)]) {
			push("remove", oldChars[i]);
			i++;
		} else {
			push("add", newChars[j]);
			j++;
		}
	}
	while (i < n) push("remove", oldChars[i++]);
	while (j < m) push("add", newChars[j++]);

	return segments;
}

/**
 * Folds short equal runs that are surrounded by changes back into the change,
 * then re-emits every change group as removal-then-addition.
 */
function mergeShortEqualRuns(segments: DiffSegment[]): DiffSegment[] {
	/** `equal` carries the shared run; a change block carries both sides. */
	interface Block {
		equal: string | null;
		remove: string;
		add: string;
	}

	// Group the flat segment list into alternating equal / change blocks.
	const blocks: Block[] = [];
	for (const segment of segments) {
		if (segment.type === "equal") {
			blocks.push({ equal: segment.text, remove: "", add: "" });
			continue;
		}
		let block = blocks[blocks.length - 1];
		if (!block || block.equal !== null) {
			block = { equal: null, remove: "", add: "" };
			blocks.push(block);
		}
		if (segment.type === "remove") block.remove += segment.text;
		else block.add += segment.text;
	}

	const merged: Block[] = [];
	for (let index = 0; index < blocks.length; index++) {
		const block = blocks[index];
		const previous = merged[merged.length - 1];
		const previousIsChange = previous !== undefined && previous.equal === null;

		if (
			block.equal !== null &&
			previousIsChange &&
			Array.from(block.equal).length < MIN_EQUAL_RUN &&
			blocks[index + 1]?.equal === null
		) {
			previous.remove += block.equal;
			previous.add += block.equal;
			continue;
		}
		if (block.equal === null && previousIsChange) {
			previous.remove += block.remove;
			previous.add += block.add;
			continue;
		}
		merged.push({ ...block });
	}

	const result: DiffSegment[] = [];
	for (const block of merged) {
		if (block.equal !== null) {
			if (block.equal) result.push({ type: "equal", text: block.equal });
			continue;
		}
		if (block.remove) result.push({ type: "remove", text: block.remove });
		if (block.add) result.push({ type: "add", text: block.add });
	}
	return result;
}

/**
 * Returns the segments that turn `oldText` into `newText`, in reading order.
 * Identical inputs yield a single `equal` segment; either side being empty
 * yields a single `remove` / `add` segment.
 */
export function diffChars(oldText: string, newText: string): DiffSegment[] {
	if (oldText === newText) return oldText ? [{ type: "equal", text: oldText }] : [];
	if (!oldText) return [{ type: "add", text: newText }];
	if (!newText) return [{ type: "remove", text: oldText }];

	const oldChars = toChars(oldText);
	const newChars = toChars(newText);
	if (oldChars.length > MAX_DIFF_LENGTH || newChars.length > MAX_DIFF_LENGTH) {
		return [
			{ type: "remove", text: oldText },
			{ type: "add", text: newText },
		];
	}

	return mergeShortEqualRuns(lcsSegments(oldChars, newChars));
}
